/**
 * A PROVIDER'S QUOTE BELONGS TO ONE TYPE AND ONE AMOUNT (owner, 21 Oct 2026 — the guaranteed income review M5).
 *
 * A quote is for a type of guaranteed income (income only, money back, or the full amount back at any time)
 * and an amount. Before this, a quote entered on one plan's card replaced our estimate on EVERY plan with a
 * guaranteed income, so a quote for an income-only annuity was applied to the money-back and full-amount-back
 * plans too (their incomes are much lower), and the field was re-ranked on incomes nobody had been offered.
 *
 * Now: a plan of the same type uses the quote, scaled pro rata to its own amount (a $200,000 plan on a
 * $100,000 quote of $6,000 a year uses $12,000); a plan of another type keeps our estimate from the
 * published rates. Used by the village, downsizing and granny flat generators.
 */

/** The type of guaranteed income a quote is for. */
export type AnnuityQuoteShape = 'guaranteed' | 'returned' | 'nil' | 'fixedTerm';

export interface AnnuityQuote {
    /** The yearly income the provider quoted. */
    income: number;
    /** The amount the quote was for. */
    amount: number;
    shape: AnnuityQuoteShape;
}

/** The type of a guaranteed income, from its product type and the share returned (0–100). */
export function annuityQuoteShapeOf(annuityType: unknown, residualPercent: unknown): AnnuityQuoteShape {
    if (annuityType === 'guaranteed') return 'guaranteed';
    if (annuityType === 'fixedTerm') return 'fixedTerm';
    return Math.round(Number(residualPercent) || 0) > 0 ? 'returned' : 'nil';
}

/** A usable quote, or null. */
export function validQuote(quote: AnnuityQuote | null | undefined): AnnuityQuote | null {
    return quote && Number(quote.income) > 0 && Number(quote.amount) > 0 ? quote : null;
}

/**
 * The quoted income for a plan's guaranteed income: the quote scaled to the plan's amount when the type is
 * the same, otherwise undefined (our estimate stands).
 */
export function quotedIncomeFor(quote: AnnuityQuote | null | undefined, annuityType: unknown, residualPercent: unknown, amount: unknown): number | undefined {
    const q = validQuote(quote);
    const a = Number(amount) || 0;
    if (!q || !(a > 0)) return undefined;
    if (annuityQuoteShapeOf(annuityType, residualPercent) !== q.shape) return undefined;
    return Math.round((q.income * a) / q.amount);
}

/** The type in plain words, as the screens name it. */
export function annuityQuoteShapeWords(shape: AnnuityQuoteShape): string {
    switch (shape) {
        case 'guaranteed': return 'full amount back at any time';
        case 'returned': return 'money back in the early years';
        case 'fixedTerm': return 'a set term';
        default: return 'income only';
    }
}

/** One line under the quote control: what the quote is for, and where it is used. */
export function annuityQuoteNote(quote: AnnuityQuote | null | undefined, money: (n: number) => string): string | null {
    const q = validQuote(quote);
    if (!q) return null;
    return `This quote is ${money(q.income)} a year for ${money(q.amount)}, ${annuityQuoteShapeWords(q.shape)}. Plans with the same type use it, scaled to their amount; plans with another type keep our estimate.`;
}
